import type { ClaudeMessage } from '../types'

/**
 * Reconciling the displayed transcript with a server replay.
 *
 * The chat socket resends conversation history on every (re)connect, and that
 * replay is only the tail the backend still keeps verbatim — older turns are
 * dropped once they have been folded into a summary. Overwriting the on-screen
 * transcript with that replay makes text the user is reading vanish, so the
 * displayed list is treated as the longer record and the replay is stitched
 * onto it instead.
 */

/**
 * Compare two transcript entries, tolerating a locally truncated tail.
 *
 * A message cut short by a dropped connection mid-stream should still match the
 * complete copy the server persisted.
 *
 * @param a - Message from one transcript.
 * @param b - Message from the other transcript.
 * @returns True when both entries represent the same turn.
 */
const isSameTurn = (a: ClaudeMessage, b: ClaudeMessage): boolean => {
  if (a.sender !== b.sender) {
    return false
  }
  if (a.content === b.content) {
    return true
  }
  if (!a.content || !b.content) {
    return false
  }
  return a.content.startsWith(b.content) || b.content.startsWith(a.content)
}

/**
 * Find how many trailing entries of `displayed` line up with the leading
 * entries of `serverTail`.
 *
 * @param displayed - Transcript currently rendered on screen.
 * @param serverTail - Transcript replayed by the server.
 * @returns Overlap length, or 0 when the two share no aligned boundary.
 */
const findOverlap = (displayed: ClaudeMessage[], serverTail: ClaudeMessage[]): number => {
  const maxOverlap = Math.min(displayed.length, serverTail.length)

  for (let overlap = maxOverlap; overlap > 0; overlap -= 1) {
    const displayedSuffix = displayed.slice(displayed.length - overlap)
    const isAligned = displayedSuffix.every((message, index) => isSameTurn(message, serverTail[index]))
    if (isAligned) {
      return overlap
    }
  }

  return 0
}

/**
 * Merge a server history replay into the transcript already on screen.
 *
 * @param displayed - Transcript currently rendered on screen.
 * @param serverTail - Transcript replayed by the server for the same conversation.
 * @returns The transcript to render: the local prefix plus the server tail when
 *   the two align, otherwise the displayed transcript unchanged.
 */
export const mergeServerHistory = (
  displayed: ClaudeMessage[],
  serverTail: ClaudeMessage[]
): ClaudeMessage[] => {
  if (displayed.length === 0) {
    return serverTail
  }
  if (serverTail.length === 0) {
    return displayed
  }

  const overlap = findOverlap(displayed, serverTail)
  if (overlap === 0) {
    // No shared boundary: the replay may be from a summarized-away stretch we
    // can no longer place. Keep what the user is reading rather than guessing.
    return displayed
  }

  return [...displayed.slice(0, displayed.length - overlap), ...serverTail]
}
